import { Collapse } from '@mui/material';
import classNames from 'classnames';
import React, { FC, HTMLAttributes, ReactNode, useState } from 'react';

import CollapseIcon from '@sbu/assets.svg/src/caret-down.svg';
import ExpandIcon from '@sbu/assets.svg/src/caret-up.svg';
import SingleChevronDownIcon from '@sbu/assets.svg/src/single-chevron-down.svg';
import { Divider } from '@sumologic/yama-ds-components';

import { Count } from 'shared/components/data/Count';

import css from './CollapseSection.module.scss';

export interface CollapseSectionProps extends HTMLAttributes<HTMLDivElement> {
  count?: number;
  expandSection?: boolean;
  initialVisible?: boolean;
  prompt: ReactNode;
  promptClassName?: string;
  promptToHide?: ReactNode;
  size?: 'small' | 'large';
  onSectionToggle?: (visible: boolean) => void;
}

// complexity 13
// eslint-disable-next-line complexity
export const CollapseSection: FC<
  React.PropsWithChildren<CollapseSectionProps>
> = ({
  children,
  count,
  expandSection,
  initialVisible = false,
  prompt,
  promptClassName,
  promptToHide,
  size = 'small',
  onSectionToggle,
  ...rest
}) => {
  const [sectionVisible, setSectionVisible] = useState(initialVisible);
  const ExpanderIcon = sectionVisible ? CollapseIcon : ExpandIcon;

  if (!sectionVisible && expandSection) {
    setSectionVisible(true);
  }

  const renderPrompt = () => {
    return (
      <div
        className={classNames(css.prompt, promptClassName, {
          [css.largePrompt]: size === 'large',
        })}
        onClick={() => {
          const visible = !sectionVisible;
          setSectionVisible(visible);

          if (onSectionToggle) {
            onSectionToggle(visible);
          }
        }}
      >
        <div>
          {count !== undefined && (
            <span>
              {(sectionVisible && promptToHide) || prompt} (
              <Count suffixPadding={false} value={count} />)
            </span>
          )}
          {count === undefined && ((sectionVisible && promptToHide) || prompt)}
          {size === 'small' && <ExpanderIcon className={css.smallIcon} />}
          {size === 'large' && (
            <div className={css.largeIconContainer}>
              <SingleChevronDownIcon
                className={classNames(css.largeIcon, {
                  [css.flipped]: sectionVisible,
                })}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div {...rest}>
      <div className={css.promptContainer} data-sumo-test='prompt-container'>
        {size === 'large' ? (
          <Divider
            sx={{
              '.MuiDivider-wrapper': {
                alignSelf: 'center',
                p: 0,
              },
              '::before, ::after': {
                borderColor: 'info.dark',
              },
            }}
          >
            {renderPrompt()}
          </Divider>
        ) : (
          renderPrompt()
        )}
      </div>
      <Collapse in={sectionVisible} timeout={750}>
        <div className={css.body}>{children}</div>
      </Collapse>
    </div>
  );
};
